import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  DEFAULT_TENANT_TIER,
  TENANT_TIER_STORAGE_KEY,
  TenantTier,
  clampToServedTier
} from '../constants/tenantTiers';

interface TierContextValue {
  tier: TenantTier;
  setTier: (tier: TenantTier) => void;
}

const TierContext = createContext<TierContextValue | null>(null);

// Storage can be unavailable (private mode, blocked site data); the switch
// must still work, it just forgets the choice on reload.
function readStoredTier(): TenantTier {
  try {
    const stored = window.localStorage.getItem(TENANT_TIER_STORAGE_KEY);
    return clampToServedTier(stored);
  } catch {
    return DEFAULT_TENANT_TIER;
  }
}

function storeTier(tier: TenantTier): void {
  try {
    window.localStorage.setItem(TENANT_TIER_STORAGE_KEY, tier);
  } catch {
    // Not persisting is acceptable: see readStoredTier.
  }
}

/** Holds the selected tenant tier (prod | dev) for every page under the app chrome. */
export function TierProvider({ children }: { children: React.ReactNode }) {
  const [tier, setTierState] = useState<TenantTier>(readStoredTier);

  const setTier = useCallback((next: TenantTier) => {
    setTierState(next);
    storeTier(next);
  }, []);

  const value = useMemo(() => ({ tier, setTier }), [tier, setTier]);
  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
}

export function useTier(): TierContextValue {
  const value = useContext(TierContext);
  if (!value) {
    throw new Error('useTier must be used inside <TierProvider>.');
  }
  return value;
}
